import { RadioGroup, RadioOption } from "@clockwise/design-system";
import { KeyboardArrowUp } from "@clockwise/design-system/icons";
import { SmartHold, SmartHoldState } from "@clockwise/schema";
import classNames from "classnames";
import React from "react";
import { track, TrackingEvents } from "../../util/analytics.util";
import { GENERIC_SMART_HOLD_DISPLAY_NAME } from "../../util/smart-hold.util";

export const PROTECT_ON_SUCCESS_MSG =
  "Your hold was updated. Clockwise will not move this hold and also decline incoming meetings.";
export const PROTECT_ON_ERROR_MSG = "Sorry, there was a problem protecting your hold.";
export const PROTECT_ON_ERROR_LOG_MSG = "Unable to protect hold";

export const PAUSE_ON_SUCCESS_MSG =
  "Your hold was updated. Clockwise will no longer move this hold, because you edited it.";
export const PAUSE_ON_ERROR_MSG = "Sorry, there was a problem updating your hold.";
export const PAUSE_ON_ERROR_LOG_MSG = "Unable to pause hold";

export const UNPAUSE_ON_SUCCESS_MSG =
  "Your hold was updated. The timing and duration of this hold may update as your schedule changes.";
export const UNPAUSE_ON_ERROR_MSG = "Sorry, there was a problem updating your hold.";
export const UNPAUSE_ON_ERROR_LOG_MSG = "Unable to unpause hold";

export type HoldName = SmartHold["__typename"];
export type HoldState = SmartHoldState["__typename"];

export type SmartHoldStatePanelProps = {
  holdId: string;
  holdName: HoldName;
  holdState: HoldState;
  onProtectHold: () => void;
  onPauseHold: () => void;
  onUnpauseHold: () => void;
  onCollapse?: () => void;
  justClickedToProtect?: boolean;
  onJustClickedToProtect?: () => void;
  badge?: React.ReactNode;
  surface?: string;
  highlightProtectOption?: boolean;
};

const FLEXIBILITY_AND_PROTECTION_LABEL: Record<HoldState, string> = {
  Unpaused: "Move as my calendar changes",
  Paused: "Do not move it",
  Protected: "Do not move it and also decline incoming meetings",
};

export function SmartHoldStatePanel({
  holdId,
  holdName,
  holdState,
  badge,
  surface,
  onProtectHold,
  onPauseHold,
  onUnpauseHold,
  onCollapse,
  justClickedToProtect,
  onJustClickedToProtect,
  highlightProtectOption = false,
}: SmartHoldStatePanelProps) {
  const [currentHoldState, setCurrentHoldState] = React.useState(holdState);

  React.useEffect(() => {
    setCurrentHoldState(holdState);
  }, [holdState]);

  const title = GENERIC_SMART_HOLD_DISPLAY_NAME[holdName];

  const handleProtectHold = () => {
    setCurrentHoldState("Protected");
    onProtectHold();
  };

  React.useEffect(() => {
    if (justClickedToProtect) {
      handleProtectHold();
      track(TrackingEvents.SMART_HOLD.STATE_UPDATED, {
        eventId: holdId,
        holdName,
        setting: "Protected",
        surface,
      });
      onJustClickedToProtect && onJustClickedToProtect();
    }
  }, [justClickedToProtect]);

  const handleUnpauseHold = () => {
    setCurrentHoldState("Unpaused");
    onUnpauseHold();
  };

  const handlePauseHold = () => {
    setCurrentHoldState("Paused");
    onPauseHold();
  };

  const onChangeFlexibilityAndProtection = (value: HoldState) => {
    if (value === "Paused") {
      handlePauseHold();
    }

    if (value === "Protected") {
      handleProtectHold();
    }

    if (value === "Unpaused") {
      handleUnpauseHold();
    }

    track(TrackingEvents.SMART_HOLD.STATE_UPDATED, {
      eventId: holdId,
      holdName,
      setting: value,
      surface,
    });
  };

  return (
    <div className="cw-border cw-border-solid cw-border-positive-interactable cw-rounded cw-bg-default">
      <div className="cw-p-3 cw-bg-positive cw-rounded-t cw-border-0 cw-border-b cw-border-solid cw-border-positive-interactable">
        <div
          className={classNames("cw-flex cw-items-center cw-gap-1 cw-justify-between", {
            "cw-cursor-pointer": onCollapse,
          })}
          onClick={onCollapse}
        >
          <div className="cw-flex cw-items-center cw-gap-1">
            <div className="cw-heading-sm cw-leading-none cw-text-positive cw-flex">
              Clockwise {title}
            </div>
            {badge}
          </div>
          {onCollapse && <KeyboardArrowUp className="cw-text-default" />}
        </div>
      </div>
      <div className="cw-p-3">
        <div className="cw-body-base cw-text-muted cw-mb-1">Flexibility & Protection</div>
        <RadioGroup
          variant="default"
          sentiment="neutral"
          onChange={(value) => onChangeFlexibilityAndProtection(value)}
          value={currentHoldState}
        >
          <RadioOption
            value={"Unpaused"}
            label={
              <span className="cw-body-base">{FLEXIBILITY_AND_PROTECTION_LABEL["Unpaused"]}</span>
            }
          />
          <RadioOption
            value={"Paused"}
            label={
              <span className="cw-body-base">{FLEXIBILITY_AND_PROTECTION_LABEL["Paused"]}</span>
            }
          />
          <div
            className={classNames({
              "cw-animate-serene-spotlight-infinite": highlightProtectOption,
            })}
          >
            <RadioOption
              value={"Protected"}
              label={
                <span className="cw-body-base">
                  {FLEXIBILITY_AND_PROTECTION_LABEL["Protected"]}
                </span>
              }
            />
          </div>
        </RadioGroup>
      </div>
    </div>
  );
}
